import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import MiniYearCalendar from "./MiniYearCalendar";
import { footprintDeEvento, relacionEventoEspacio } from "../../utils/espacios";
import { diasDeEvento } from "./dias";
import { useDayPopover } from "../../hooks/useDayPopover";

// Rojo de "ocupado", el mismo que ya usaban la vista de año y el calendario
// de leads.
const OCUPADO = "#f87171";

// Vista de año. En un venue multiespacio el rojo binario mentía: bastaba un
// evento en un salón para pintar el día entero, así que un año con seis
// salones se veía lleno aunque estuviera casi vacío.
//
// A 24px por día no caben carriles (para eso está la vista de mes); lo que sí
// se lee es la FRACCIÓN ocupada, que se pinta como un nivel que sube. Un día
// con 2 de 6 espacios tomados se ve a un tercio, y solo los días sin NADA
// libre se ven llenos. Con un espacio (o ninguno) la fracción es 0 ó 1 y se
// comporta como siempre.
//
// La leyenda del encabezado filtra qué espacios cuentan: aislar Salón B
// convierte el año en la agenda de Salón B.

// Solo `event` y `bloqueo` ocupan el venue — un scouting o una reunión no
// impiden vender la fecha.
const TIPOS_QUE_OCUPAN = new Set(["event", "bloqueo"]);

export default function YearView({
  year,
  events = [],
  espacios = [],
  espacioColor = {},
  timeZone = "America/Mexico_City",
  onMonthClick,
  onEventClick,
}) {
  const {
    popover,
    show: showPopover,
    scheduleHide,
    cancelHide,
    close: closePopover,
  } = useDayPopover();
  const porDia = useMemo(() => {
    const mapa = new Map();
    events.forEach((ev) => {
      const props = ev.extendedProps || ev;
      if (props.status === "cancelado") return;
      const tipo = String(props.eventType || "").toLowerCase();
      if (!TIPOS_QUE_OCUPAN.has(tipo)) return;
      diasDeEvento(ev, timeZone).forEach((dia) => {
        if (!mapa.has(dia)) mapa.set(dia, []);
        mapa.get(dia).push(ev);
      });
    });
    return mapa;
  }, [events, timeZone]);

  // Desglose por día: qué espacio está tomado, por qué evento y si el evento
  // ocurre AHÍ o solo lo deja sin vender (misma distinción que los carriles
  // de la vista de mes).
  const detalle = useMemo(() => {
    return (iso) => {
      const evs = porDia.get(iso);
      if (!evs || evs.length === 0) return null;
      if (espacios.length === 0) return { sinEspacios: true, eventos: evs };
      const ocupados = [];
      espacios.forEach((esp) => {
        const hits = evs
          .map((ev) => ({
            ev,
            rel: relacionEventoEspacio(
              ev.extendedProps?.espaciosIds || [],
              footprintDeEvento(ev),
              esp
            ),
          }))
          .filter((x) => x.rel);
        if (hits.length) ocupados.push({ esp, hits });
      });
      if (ocupados.length === 0) return null;
      return {
        ocupados,
        libres: espacios.filter((e) => !ocupados.some((o) => o.esp.id === e.id)),
      };
    };
  }, [porDia, espacios]);

  const dayInfo = useMemo(() => {
    return (iso) => {
      const d = detalle(iso);
      if (!d) return null;
      // Venue sin espacios reservables: cualquier evento llena el día, que es
      // exactamente el comportamiento de antes de multiespacio.
      if (d.sinEspacios) {
        return {
          fill: 1,
          color: OCUPADO,
          clickable: true,
          tooltip: `${d.eventos.length} evento${d.eventos.length === 1 ? "" : "s"}`,
        };
      }
      return {
        fill: d.ocupados.length / espacios.length,
        color: OCUPADO,
        clickable: true,
        tooltip: `${d.ocupados.length} de ${espacios.length} espacios ocupados`,
      };
    };
  }, [detalle, espacios]);

  return (
    <div className="p-4">
      {espacios.length > 0 && (
        <p className="mb-4 text-center text-xs text-zinc-500 dark:text-zinc-400">
          El relleno de cada día es la proporción de espacios ocupados — un día
          lleno del todo se ve sólido. Usa la leyenda de arriba para aislar un
          espacio.
        </p>
      )}
      <MiniYearCalendar
        year={year}
        dayInfo={dayInfo}
        onMonthClick={onMonthClick}
        onDayClick={(iso, rect) =>
          showPopover(iso, rect, detalle(iso), { inmediato: true })
        }
        onDayEnter={(iso, rect) => showPopover(iso, rect, detalle(iso))}
        onDayLeave={scheduleHide}
      />

      {/* Detalle del día. El clic en un mes navega, así que el popover para
          la propagación: si no, mirar un día te cambiaba de vista. */}
      {popover &&
        createPortal(
          <div
            className="fixed z-[95] max-h-72 w-64 overflow-y-auto rounded-xl border border-zinc-200 bg-white p-3 shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
            style={{ left: popover.x, top: popover.y }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
          >
            <div className="mb-1.5 text-xs font-semibold text-zinc-500">
              {popover.key}
            </div>

            {popover.data.sinEspacios ? (
              <div className="space-y-1">
                {popover.data.eventos.map((ev) => (
                  <EventoFila
                    key={ev.id}
                    ev={ev}
                    onClick={onEventClick}
                    onNavigate={closePopover}
                  />
                ))}
              </div>
            ) : (
              <>
                {/* Agrupado por EVENTO (no por espacio): una boda que toma
                    cuatro salones es una fila con cuatro chips, no cuatro
                    filas repitiendo el mismo nombre. */}
                <div className="space-y-2">
                  {agrupaPorEvento(popover.data.ocupados).map(
                    ({ ev, espaciosDe }) => (
                      <div key={ev.id}>
                        <EventoFila
                          ev={ev}
                          onClick={onEventClick}
                          onNavigate={closePopover}
                        />
                        <div className="ml-1 mt-0.5 flex flex-wrap gap-1">
                          {espaciosDe.map(({ esp, rel }) => (
                            <span
                              key={esp.id}
                              title={
                                rel === "consecuencia"
                                  ? `${esp.nombre}: no disponible por este evento`
                                  : esp.nombre
                              }
                              className={`inline-flex items-center gap-1 rounded-full border border-zinc-200 px-1.5 py-px text-[10px] dark:border-zinc-600 ${
                                rel === "consecuencia"
                                  ? "italic text-zinc-400"
                                  : "text-zinc-600 dark:text-zinc-300"
                              }`}
                            >
                              <span
                                className="h-1.5 w-1.5 shrink-0 rounded-sm"
                                style={{
                                  backgroundColor:
                                    espacioColor[esp.id] || OCUPADO,
                                  opacity: rel === "consecuencia" ? 0.45 : 1,
                                }}
                              />
                              {esp.nombre}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
                <div className="mt-2 border-t border-zinc-100 pt-1.5 text-[10px] text-zinc-400 dark:border-zinc-700">
                  {popover.data.libres.length > 0
                    ? `Libres: ${popover.data.libres.map((e) => e.nombre).join(", ")}`
                    : "Sin espacios libres este día"}
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

// Invierte el desglose por espacio a "por evento": [{ev, espaciosDe: [{esp,
// rel}]}] en orden de aparición. `rel === "consecuencia"` viaja con cada
// espacio — el evento no ocurre ahí, solo lo deja sin vender.
function agrupaPorEvento(ocupados) {
  const porEvento = new Map();
  ocupados.forEach(({ esp, hits }) => {
    hits.forEach(({ ev, rel }) => {
      if (!porEvento.has(ev.id)) porEvento.set(ev.id, { ev, espaciosDe: [] });
      porEvento.get(ev.id).espaciosDe.push({ esp, rel });
    });
  });
  return Array.from(porEvento.values());
}

// Una fila de evento del popover. `indirecto` = el evento NO ocurre en ese
// espacio, solo lo deja sin vender (contención parcial o incompatibilidad).
function EventoFila({ ev, indirecto = false, onClick, onNavigate }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onNavigate?.();
        onClick?.(ev);
      }}
      className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left transition hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
    >
      <span
        className={`min-w-0 flex-1 truncate text-[11px] ${
          indirecto
            ? "italic text-zinc-400"
            : "text-zinc-700 dark:text-zinc-200"
        }`}
      >
        {indirecto ? `No disponible por ${ev.title}` : ev.title}
      </span>
      {onClick && (
        <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0 text-zinc-400" />
      )}
    </button>
  );
}
